import React, { useEffect, useRef, useState } from 'react'
import { decompressFrames, parseGIF } from 'gifuct-js'

interface GifPlayerProps {
  src: string
  alt?: string
  className?: string
  onClick?: () => void
  width?: number
  height?: number
}

export const GifPlayer: React.FC<GifPlayerProps> = ({
  src,
  alt = '',
  className = '',
  onClick,
  width,
  height,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const animationRef = useRef<number | undefined>(undefined)
  const framesRef = useRef<any[]>([])
  const currentFrameRef = useRef(0)
  const startTimeRef = useRef(0)

  useEffect(() => {
    let isMounted = true

    const loadGif = async () => {
      try {
        setIsLoading(true)
        setError(null)

        console.log('GifPlayer: Loading GIF from:', src)

        // Fetch the GIF data
        const response = await fetch(src)
        if (!response.ok) {
          throw new Error(`Failed to fetch GIF: ${response.statusText}`)
        }

        const arrayBuffer = await response.arrayBuffer()
        console.log('GifPlayer: GIF loaded, size:', arrayBuffer.byteLength)

        // Parse and decompress the GIF
        const gif = parseGIF(arrayBuffer)
        const frames = decompressFrames(gif, true)

        console.log('GifPlayer: GIF parsed, frames:', frames.length)

        if (!isMounted) return

        framesRef.current = frames
        setIsLoading(false)

        // Set canvas dimensions
        const canvas = canvasRef.current
        if (canvas && frames.length > 0) {
          const firstFrame = frames[0]
          canvas.width = width || firstFrame.dims.width
          canvas.height = height || firstFrame.dims.height
          console.log('GifPlayer: Canvas dimensions set:', canvas.width, 'x', canvas.height)
        }

        // Start animation
        startAnimation()
      } catch (err) {
        console.error('GifPlayer: Error loading GIF:', err)
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load GIF')
          setIsLoading(false)
        }
      }
    }

    const startAnimation = () => {
      const canvas = canvasRef.current
      if (!canvas || framesRef.current.length === 0) return

      const ctx = canvas.getContext('2d')
      if (!ctx) return

      startTimeRef.current = performance.now()

      const animate = (currentTime: number) => {
        if (!isMounted) return

        const frames = framesRef.current
        if (frames.length === 0) return

        // Calculate which frame to show based on elapsed time
        let elapsed = currentTime - startTimeRef.current
        let frameIndex = 0
        let frameTime = 0

        // Find the current frame based on timing
        for (let i = 0; i < frames.length; i++) {
          const frame = frames[i]
          const delay = frame.delay || 100 // Default 100ms if no delay specified

          if (elapsed < frameTime + delay) {
            frameIndex = i
            break
          }
          frameTime += delay
        }

        // If we've gone through all frames, restart the loop (infinite loop)
        if (elapsed >= frameTime) {
          startTimeRef.current = currentTime
          frameIndex = 0
          elapsed = 0
        }

        // Only update if frame changed
        if (frameIndex !== currentFrameRef.current) {
          currentFrameRef.current = frameIndex
          const frame = frames[frameIndex]

          // Clear canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height)

          // Draw the frame
          if (frame.patch) {
            try {
              const imageData = new ImageData(frame.patch, frame.dims.width, frame.dims.height)
              ctx.putImageData(imageData, frame.dims.left, frame.dims.top)
            } catch (err) {
              console.error('Error drawing frame:', err)
            }
          }
        }

        animationRef.current = requestAnimationFrame(animate)
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    loadGif()

    return () => {
      isMounted = false
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [src, width, height])

  if (error) {
    // Fallback to regular img tag if GIF parsing fails
    return (
      <img
        className={className}
        src={src}
        alt={alt}
        onClick={onClick}
        style={{
          width: width ? `${width}px` : '100%',
          height: height ? `${height}px` : 'auto',
          maxWidth: '100%',
          display: 'block',
        }}
      />
    )
  }

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <span className='text-gray-500 text-sm'>Loading GIF...</span>
      </div>
    )
  }

  return (
    <canvas
      ref={canvasRef}
      className={className}
      onClick={onClick}
      style={{
        width: width ? `${width}px` : '100%',
        height: height ? `${height}px` : 'auto',
        maxWidth: '100%',
        display: 'block',
      }}
    />
  )
}

export default GifPlayer
